import { gql, TypedDocumentNode } from '@common/hooks';
import { UploadFile } from 'antd';
import {
  DisputeListItemFragmentType,
  DisputeReviewFragment,
} from '@pay-bill/graphql/fragments';
import {
  FullValidationErrorFragment,
  FullValidationErrorFragmentType,
} from '@common/graphql/fragments';
import {
  DisputeType,
  SimpleFileFragment,
  SimpleFileFragmentType,
} from '@common/graphql';

export const PerformDisputeAction: TypedDocumentNode<
  PerformDisputeActionMutationResponse,
  PerformDisputeActionMutationArgs
> = gql`
  mutation PerformDisputeAction(
    $disputeId: ID!
    $input: PerformDisputeActionInput!
  ) {
    performDisputeAction(disputeId: $disputeId, input: $input) {
      dispute {
        ...DisputeReviewFragment
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${DisputeReviewFragment}
  ${FullValidationErrorFragment}
`;

export type PerformDisputeActionMutationArgs = {
  disputeId: string;
  input: {
    action:
      | 'approve'
      | 'accept'
      | 'edit'
      | 'reject'
      | 'reopen'
      | 'retract'
      | 'redirect_to_organization'
      | 'redirect_to_supplier'
      | 'retract_redirect';
    newHours?: number;
    billingAmountCents?: number;
    newPayCode?: string;
    comment?: string;
    finalRateCents?: number;
    rejectionReason?: string;
    addedFiles?: UploadFile[];
    removedFiles?: string[];
  };
};

export type PerformDisputeActionMutationResponse = {
  performDisputeAction: {
    error?: string;
    dispute?: DisputeListItemFragmentType;
  };
};

export const DisputeCreateMutation = gql`
  mutation DisputeCreateMutation(
    $staffingRequestAssignmentId: ID!
    $invOrRinvId: ID!
    $entityType: String!
    $worksInvoiceItemId: ID
    $input: CreateDisputeMutationInput!
    $files: [FileUploadInput!]
  ) {
    createDispute(
      staffingRequestAssignmentId: $staffingRequestAssignmentId
      invOrRinvId: $invOrRinvId
      entityType: $entityType
      worksInvoiceItemId: $worksInvoiceItemId
      input: $input
      files: $files
    ) {
      dispute {
        id
        ...DisputeReviewFragment
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${DisputeReviewFragment}
  ${FullValidationErrorFragment}
`;

export type DisputeCreateMutationResponse = {
  createDispute: {
    dispute: DisputeListItemFragmentType | null;
    errors: FullValidationErrorFragmentType[] | null;
  };
};

export type DisputeCreateMutationArg = {
  staffingRequestAssignmentId: string;
  invOrRinvId: string;
  entityType: string | undefined;
  worksInvoiceItemId: string | undefined;
  files: UploadFile[];
  input: {
    creationReason: string;
    disputeType: DisputeType;
    newDate: string;
    newHours: number;
    newPayCode: string;
    finalRateCents: number;
  };
};

export const DisputeExportMutation = gql`
  mutation CreateDisputeExport($input: CreateDisputeExportInput!) {
    result: createDisputeExport(input: $input) {
      file: exportFile {
        ...SimpleFileFragment
      }
      disputes {
        id
        exported
      }
    }
  }
  ${SimpleFileFragment}
`;

export type DisputeExportResponse = {
  result: {
    file: SimpleFileFragmentType;
  };
  disputes: {
    id: string;
    exported: boolean;
  }[];
};

export type DisputeExportArgs = {
  input: {
    entityId?: string;
    entityType?: 'invoice' | 'reverse_invoice';
    format: 'tsv' | 'csv' | 'xlsx';
    disputeIds?: string[];
  };
};
